import { createApi, retry } from '@reduxjs/toolkit/dist/query/react';
import { axiosBaseQuery } from '@urc-ui/store/api/base';
import { HttpMethod, Tag } from '@urc-ui/store/api/types';
import { getAcceptLanguageHeader, resolveAdvertisingApiBaseUrl } from '@urc-ui/store/api/ross/utils';
import { exponentialBackoffWithJitter, getFrbAuthorizationHeaders } from '@flexible-reports/store/api/base';
import {
  QueryReportingFieldRelationshipsRequest,
  QueryReportingFieldRequest,
  ReportingFieldRelationshipsResponse,
  ReportingFieldSuccessResponse
} from '@flexible-reports/store/api/metadata/api-types';
import { transformRelationshipsFromApiResponse } from '@flexible-reports/store/api/metadata/transformers/transform-relationships-response';
import { transformFromApiResponse } from '@flexible-reports/store/api/metadata/transformers/transform-from-api-response';
import { FieldType, ReportingInterface } from '@flexible-reports/store/api/metadata/types';

export const metadataApi = createApi({
  reducerPath: 'metadataApi',
  baseQuery: retry(
    axiosBaseQuery({
      baseUrl: resolveAdvertisingApiBaseUrl()
    }),
    exponentialBackoffWithJitter({ baseDelay: 50, maxDelay: 5000, maxRetries: 5 })
  ),
  tagTypes: [Tag.METADATA],
  endpoints: builder => ({
    queryReportingFields: builder.query({
      query: (request: QueryReportingFieldRequest) => {
        return {
          url: `/adsApi/v1/query/reportingFields`,
          method: HttpMethod.POST,
          headers: {
            ...getFrbAuthorizationHeaders(),
            ...getAcceptLanguageHeader()
          },
          data: request
        };
      },
      transformResponse: (response: ReportingFieldSuccessResponse) => {
        return {
          metadata: transformFromApiResponse(response.reportingFields),
          nextToken: response.nextToken
        };
      },
      keepUnusedDataFor: 15 * 60 // cache for 15 minutes
    }),
    queryReportingFieldRelationships: builder.query({
      query: (request: QueryReportingFieldRelationshipsRequest) => {
        return {
          url: `/adsApi/v1/query/reportingFieldRelationships`,
          method: HttpMethod.POST,
          headers: {
            ...getFrbAuthorizationHeaders(),
            ...getAcceptLanguageHeader()
          },
          data: request
        };
      },
      transformResponse: (response: ReportingFieldRelationshipsResponse) => {
        return transformRelationshipsFromApiResponse(response.reportingFieldRelationships);
      },
      keepUnusedDataFor: 15 * 60 // cache for 15 minutes
    })
  })
});

export const { useQueryReportingFieldsQuery, useQueryReportingFieldRelationshipsQuery } = metadataApi;
export const selectReportingFieldsResult = metadataApi.endpoints?.queryReportingFields.select({
  typeFilter: { include: [FieldType.DIMENSION_ATTRIBUTE, FieldType.METRIC] }
});
export const selectRelationshipsResult = metadataApi.endpoints?.queryReportingFieldRelationships.select({
  reportingInterfaceFilter: { include: [ReportingInterface.REPORT] }
});
